/**
 * SchemaFileUploadField — drag-and-drop file picker rendered by
 * ``JsonSchemaForm`` when a schema field carries ``x-widget: "file_upload"``.
 *
 * Wraps the shared ``FileUploadDropzone`` and base64-encodes every dropped
 * file into ``{name, mime_type, bytes_b64}`` shapes so the launch payload
 * carries them across the wire to the substrate. Identical encoding /
 * limits / sibling-keep semantics to ``HitlFileUploadPanel`` — the
 * helpers are duplicated here on purpose for now (see TODO at the top of
 * the helpers block) to avoid touching the HITL panel in the same PR;
 * a follow-up should extract them into ``~/common/workflows/fileUploadHelpers``.
 */

import { useState } from "react";
import { FileUploadDropzone } from "~/common/components/FileUploadDropzone";

export interface SchemaFileUploadValue {
  name: string;
  mime_type: string;
  bytes_b64: string;
}

export interface SchemaFileUploadFieldProps {
  /** Current value — array of encoded files (or empty array). */
  value: SchemaFileUploadValue[] | undefined;
  /** Called with the next array whenever files change. */
  onChange: (next: SchemaFileUploadValue[]) => void;
  /** Disable the dropzone (e.g. while the parent form is submitting). */
  disabled?: boolean;
}

// TODO(refactor): extract these constants + fileToBase64 into
// ~/common/workflows/fileUploadHelpers so HitlFileUploadPanel can share
// them. Duplicated here to keep this PR focused on the launch-form widget.
const ACCEPTED_FILE_TYPES = [".pdf", ".png", ".jpg", ".jpeg", ".docx", ".doc"];
const MAX_FILE_BYTES = 25 * 1024 * 1024;
const MAX_FILES = 10;

export function SchemaFileUploadField({
  value,
  onChange,
  disabled,
}: SchemaFileUploadFieldProps) {
  const [error, setError] = useState<string>("");
  // The dropzone wants a FileList-shaped value for its display block;
  // we keep ``files: File[]`` internally and reconstruct shape on demand.
  // We can't recreate File objects from the {name, mime_type, bytes_b64}
  // value (FileReader is one-way), so internal File[] tracks files
  // dropped THIS render. On re-render with persisted base64 values we
  // still show the count via the encoded array length, just not the
  // exact File[] (acceptable for a launch form — the AM sees their
  // chosen file names below the dropzone, sourced from the encoded
  // values, and the count badge is correct).
  const [files, setFiles] = useState<File[]>([]);

  const encoded: SchemaFileUploadValue[] = value ?? [];

  const handleFileChange = (newFiles: FileList) => {
    setError("");
    const accepted: File[] = [];
    const rejectedNames: string[] = [];
    Array.from(newFiles).forEach((f) => {
      if (f.size > MAX_FILE_BYTES) {
        rejectedNames.push(f.name);
        return;
      }
      accepted.push(f);
    });
    // Sibling-keep semantics (T4+T5 reviewer HIGH): one oversized file
    // does not discard the rest of the drop.
    const combined = [...files, ...accepted];
    if (combined.length > MAX_FILES) {
      setError(`Up to ${MAX_FILES} files at a time.`);
      return;
    }
    if (rejectedNames.length > 0) {
      const list = rejectedNames.map((n) => `"${n}"`).join(", ");
      setError(
        `${list} exceeded the 25 MB per-file limit and ${
          rejectedNames.length === 1 ? "was" : "were"
        } skipped.`,
      );
    }
    // Encode every accepted file then publish the merged array upward.
    Promise.all(combined.map(encodeFile)).then((nextEncoded) => {
      setFiles(combined);
      onChange(nextEncoded);
    });
  };

  const handleFileRemove = (index: number) => {
    setError("");
    const nextFiles = files.filter((_, i) => i !== index);
    const nextEncoded = encoded.filter((_, i) => i !== index);
    setFiles(nextFiles);
    onChange(nextEncoded);
  };

  const handleClearAll = () => {
    setError("");
    setFiles([]);
    onChange([]);
  };

  // Construct a FileList-like view of the encoded values for the
  // dropzone's display block. It only reads ``.length`` and iterates via
  // ``Array.from(...)``, so a File[]-shaped array works — but we have to
  // synthesise minimal File objects from the encoded values when ``files``
  // is empty (e.g. after a re-mount).
  const displayFiles: File[] =
    files.length > 0
      ? files
      : encoded.map(
          (e) =>
            new File([new Uint8Array(0)], e.name, { type: e.mime_type || "" }),
        );

  return (
    <div data-testid="schema-file-upload-field">
      <FileUploadDropzone
        onFileChange={handleFileChange}
        uploadedFiles={displayFiles as unknown as FileList}
        onFileRemove={handleFileRemove}
        onClearAll={handleClearAll}
        acceptedFileTypes={ACCEPTED_FILE_TYPES}
        maxFiles={MAX_FILES}
        fileUploadMaxSize={MAX_FILE_BYTES}
        error={error}
        // FileUploadDropzone does not have a ``disabled`` prop; the parent
        // form's submitting state mostly matters via the disabled submit
        // button (the dropzone briefly accepting another file mid-submit
        // is harmless — the trigger has already snapshotted the values).
        // Track the flag so a future dropzone variant could honour it.
        className={disabled ? "opacity-60 pointer-events-none" : undefined}
      />
    </div>
  );
}

async function encodeFile(file: File): Promise<SchemaFileUploadValue> {
  const bytes_b64 = await fileToBase64(file);
  return {
    name: file.name,
    mime_type: file.type || "application/octet-stream",
    bytes_b64,
  };
}

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error("FileReader failed"));
    reader.readAsDataURL(file);
  });
}
